import { ATTRIBUTE_NAME_CONTACT_LENSES } from './ATTRIBUTE_NAME'
import {
  type AttributeGroupData,
  type AttributeSelectionRenderGroups,
} from './AttributeGroup'

/**
 * prepares groups for visual rendering.
 *
 * filters out groups that should not be displayed.
 * groups on UI level:
 * attribute groups can be grouped for rendering,
 * e.g. contact_lense attributes are grouped together on UI level.
 * this function returns an array with the groups to render
 * @example
 * ['packageSize', 'dioptrien', 'zylinder', 'color']
 * returns [
 *   {group: packageSize},
 *   {contactLensesGroups: [dioptrien, zylinder]},
 *   {group: color}
 * ]
 * @example
 * [packageSize, color]
 * return [
 *   {group: packageSize},
 *   {group: color}
 * ]
 *
 * this is needed because lenses group must keep its order of appearance.
 */
export const prepareRenderGroups = (
  groups: (AttributeGroupData | undefined)[],
): AttributeSelectionRenderGroups => {
  const result: AttributeSelectionRenderGroups = []

  const visibleGroups = groups.filter((it): it is AttributeGroupData => {
    return Boolean(it?.shouldRenderGroup)
  })

  const indexOfFirstAttributeWithContactLenses = visibleGroups.findIndex(
    (group) => {
      return group.metaAttributeData.group === ATTRIBUTE_NAME_CONTACT_LENSES
    },
  )

  const contactLensesGroups = visibleGroups.filter(
    (group): group is AttributeGroupData => {
      return group.metaAttributeData.group === ATTRIBUTE_NAME_CONTACT_LENSES
    },
  )

  const groupsWithoutContactLenses = visibleGroups.filter((group) => {
    return group.metaAttributeData.group !== ATTRIBUTE_NAME_CONTACT_LENSES
  })

  for (const it of groupsWithoutContactLenses) {
    result.push({ group: it })
  }

  // insert contact lenses groups at the position of the first contact lenses attribute in original array,
  if (indexOfFirstAttributeWithContactLenses !== -1) {
    result.splice(indexOfFirstAttributeWithContactLenses, 0, {
      contactLensesGroups,
    })
  }

  return result
}
